import { getEnv } from 'core/helpers';
import { getLocalStorageData, setLocalStorageData } from 'helpers/local-storage';
import {
  FIREBASE_CONFIG,
  VAPID_KEY,
  FCM_TOKEN_LS_KEY,
  PARTY_ID_LS_KEY,
  NOTIFICATION_PERMISSION_TYPES,
} from 'src/constants';

const CIO_SITE_ID = getEnv('CIO_SITE_ID');

const getCioInstance = () => {
  if (!CIO_SITE_ID) {
    console.log('Customer IO: site id not found!');

    return;
  }

  let cioInstance;

  if (window && window._cio) {
    try {
      cioInstance = window._cio;
    } catch (err) {
      console.log(err);
    }
  }

  // eslint-disable-next-line consistent-return
  return cioInstance;
};

export const isUserIdentifiedInCio = () => {
  const cioInstance = getCioInstance();

  if (!cioInstance) {
    return false;
  }

  const cioId = cioInstance._findCustomer?.();

  return !!cioId;
};

const getDeviceInfo = (fcmToken) => {
  const getMobilePlatform = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'ios';
    }

    return 'android';
  };

  return {
    token: fcmToken,
    platform: getMobilePlatform(),
    attributes: {
      device_locale: navigator.language
    }
  };
};

export const initializeFirebaseMessaging = ({ partyId, setCioFcmInfo, onTokenReceived }) => {
  if (!window.firebase) {
    console.error('Firebase script is not connected');

    return;
  }

  window.firebase.initializeApp(FIREBASE_CONFIG);

  const messaging = window.firebase.messaging();

  messaging.getToken({ vapidKey: VAPID_KEY })
    .then((fcmToken) => {
      if (fcmToken) {
        if (onTokenReceived) {
          onTokenReceived(partyId, fcmToken, setCioFcmInfo);
        }
      } else {
        console.error('No FCM token retrieved.');
      }
    })
    .catch((e) => {
      console.log('FCM error', e);
    });

  const createBrowserStandardNotification = ({ title, options }) => {
    if (window.swRegistration) {
      window.swRegistration.showNotification(title, options);
    } else {
      // eslint-disable-next-line no-new
      new Notification(title, options);
    }
  };

  // Handle incoming messages in foreground
  messaging.onMessage((payload) => {
    const notificationTitle = payload?.notification?.title;
    const notificationBodyText = payload?.notification?.body;
    const notificationTag = payload?.messageId;

    const notificationOptions = {
      body: notificationBodyText,
      tag: notificationTag,
    };

    createBrowserStandardNotification({
      title: notificationTitle,
      options: notificationOptions,
    });
  });
};

export const checkNotificationPermission = ({
  onPermissionDefault,
  onPermissionGranted,
  onPermissionDenied,
}) => {
  if (!('Notification' in window)) {
    console.error('Notifications are not supported by this browser.');

    return;
  }

  const currentPermission = Notification.permission;

  if (currentPermission === NOTIFICATION_PERMISSION_TYPES.DEFAULT) {
    if (onPermissionDefault) {
      onPermissionDefault();
    }

    return;
  }

  if (currentPermission === NOTIFICATION_PERMISSION_TYPES.GRANTED) {
    if (onPermissionGranted) {
      onPermissionGranted();
    }

    return;
  }

  // denied
  if (onPermissionDenied) {
    onPermissionDenied();
  }
};

export const askNotificationPermission = ({
  onPermissionGranted,
  onPermissionDenied
}) => {
  Notification.requestPermission()
    .then((permission) => {
      if (permission === NOTIFICATION_PERMISSION_TYPES.GRANTED) {
        if (onPermissionGranted) {
          onPermissionGranted();
        }
      } else {
        if (onPermissionDenied) {
          onPermissionDenied();
        }

        console.warn('User denied notification permission');
      }
    });
};

export const handleCioFcmInfo = (partyId, fcmToken, setCioFcmInfo) => {
  const lastFcmToken = getLocalStorageData(FCM_TOKEN_LS_KEY);
  const lastPartyId = getLocalStorageData(PARTY_ID_LS_KEY);

  if (fcmToken !== lastFcmToken || partyId !== lastPartyId) {
    const deviceInfo = getDeviceInfo(fcmToken);

    setCioFcmInfo({ partyId, deviceInfo });

    setLocalStorageData(FCM_TOKEN_LS_KEY, fcmToken);
    setLocalStorageData(PARTY_ID_LS_KEY, partyId);
  }
};
